import getStore from 'src/modules/store';
import Plans from 'src/security/plans';
import { taskKanbanGestorFeatureGrantsKanbanAdminEquivalent } from 'src/security/taskKanbanGestorAccess';

const plans = Plans.values;

export default class PermissionChecker {
  currentTenant;
  currentUser;
  /** Si se pasó 3.er argumento, se usa ese valor (puede ser null). Si no, se lee Redux. */
  _tenantAccessContextOverride;
  _hasTenantAccessContextOverride;

  constructor(currentTenant, currentUser, tenantAccessContextOverride?) {
    this.currentTenant = currentTenant;
    this.currentUser = currentUser;
    this._hasTenantAccessContextOverride = arguments.length >= 3;
    this._tenantAccessContextOverride = tenantAccessContextOverride;
  }

  getResolvedTenantAccessContext() {
    if (this._hasTenantAccessContextOverride) {
      return this._tenantAccessContextOverride ?? null;
    }
    try {
      return getStore()?.getState()?.auth?.tenantAccessContext ?? null;
    } catch {
      return null;
    }
  }

  get currentUserRolesIds() {
    if (!this.currentUser || !this.currentUser.tenants) {
      return [];
    }

    const tenant = this.currentUser.tenants
      .filter(
        (tenantUser) => tenantUser.status === 'active',
      )
      .find(
        (tenantUser) =>
          tenantUser.tenant.id === this.currentTenant.id,
      );

    if (!tenant) {
      return [];
    }

    return tenant.roles;
  }

  /**
   * Permisos explícitos del rol custom (`tenantAccessContext.customPermissionIds`).
   * Para reglas “solo custom si hay customRole” en UI acotada, ver `customRoleScopedPermissionUi`.
   */
  customPermissionIdsMatch(permission) {
    const ids = this.getResolvedTenantAccessContext()?.customPermissionIds;
    if (!Array.isArray(ids) || !ids.length || !permission?.id) {
      return false;
    }
    return ids.includes(permission.id);
  }

  match(permission) {
    if (!permission) {
      return true;
    }

    if (!this.planMatchOneOf(permission.allowedPlans)) {
      return false;
    }

    if (this.rolesMatchOneOf(permission.allowedRoles)) {
      return true;
    }

    const ctx = this.getResolvedTenantAccessContext();
    if (
      taskKanbanGestorFeatureGrantsKanbanAdminEquivalent(
        ctx?.featureKeys,
        permission,
      )
    ) {
      return true;
    }

    return this.customPermissionIdsMatch(permission);
  }

  lockedForCurrentPlan(permission) {
    if (!permission) {
      return false;
    }

    const hasRole = this.rolesMatchOneOf(permission.allowedRoles);
    const hasCustom = this.customPermissionIdsMatch(permission);
    const ctx = this.getResolvedTenantAccessContext();
    const hasKanbanGestorFeature =
      taskKanbanGestorFeatureGrantsKanbanAdminEquivalent(
        ctx?.featureKeys,
        permission,
      );
    if (!hasRole && !hasCustom && !hasKanbanGestorFeature) {
      return false;
    }

    return !this.planMatchOneOf(permission.allowedPlans);
  }

  rolesMatchOneOf(arg) {
    if (!this.currentUserRolesIds) {
      return false;
    }

    if (!arg) {
      return false;
    }

    if (Array.isArray(arg)) {
      if (!arg.length) {
        return false;
      }

      return arg.some((role) =>
        this.currentUserRolesIds.includes(role),
      );
    }

    return this.currentUserRolesIds.includes(arg);
  }

  planMatchOneOf(arg) {
    if (!this.currentTenantPlan) {
      return false;
    }

    if (!arg) {
      return false;
    }

    if (Array.isArray(arg)) {
      if (!arg.length) {
        return false;
      }

      return arg.some(
        (plan) => plan === this.currentTenantPlan,
      );
    }

    return arg === this.currentTenantPlan;
  }

  get currentTenantPlan() {
    if (!this.currentTenant) {
      return plans.free;
    }

    if (!this.currentTenant.plan) {
      return plans.free;
    }

    return this.currentTenant.plan;
  }

  get isEmptyTenant() {
    if (!this.isAuthenticated) {
      return true;
    }

    if (!this.currentUser.tenants) {
      return true;
    }

    return !this.currentUser.tenants.some(
      (tenant) => tenant.status === 'active',
    );
  }

  get isEmptyPermissions() {
    if (!this.isAuthenticated) {
      return true;
    }

    if (!this.currentUser.tenants) {
      return true;
    }

    const tenant = this.currentUser.tenants.find(
      (tenant) => tenant.status === 'active',
    );

    if (!tenant) {
      return true;
    }

    return !tenant.roles || !tenant.roles.length;
  }

  get isAuthenticated() {
    return (
      Boolean(this.currentUser) &&
      Boolean(this.currentUser.id)
    );
  }

  get isEmailVerified() {
    if (!this.isAuthenticated) {
      return false;
    }

    return this.currentUser.emailVerified;
  }
}
